import * as React from "react";
import { DisplayRemoveOutdatedTasksLink } from "source/app/presentation/MainPanel/Workflows/RenderChildNodes/RemoveOutdatedTasks/RemoveOutdatedTasks";
import { RenderChildNode } from "source/app/presentation/MainPanel/Workflows/RenderChildNodes/Single/RenderChildNode";
import { ScrollToBottomIfLarge } from "source/app/services/UI/UI";
import { SetSortTarget } from "../RootNodeName/WorkflowsFloatingTools/SortNodes/SortNodes";
import { DisplayViewChronologicalNote } from "./DisplayViewChronologicalNote";
import { IRenderChildNodesOptions } from "./IRenderChildNodesOptions";
import { IRenderChildNodesState } from "./IRenderChildNodesState";
import { LoadChildNodeData } from "./LoadChildNodeData";
import { usePrevious } from "source/app/services/Shared/Hooks/usePrevious";
import { ShareState } from "source/app/services/UI/State/ShareState";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { CompleteAllTodayNodesLink } from "./CompleteAllTodayNodesLink";
import { LogWorkflowNavigation } from "source/app/services/Workflows/WorkflowNavigationDiagnostics";

export function RenderChildNodes(props: IRenderChildNodesOptions): JSX.Element {
    const { visibleNodes, rootNode, level, sortMode, sortSource } = props;

    const [state, setState] = React.useState<IRenderChildNodesState>({
        immediateChildren: [],
        sortedNodes: [],
    });
    const nodesToDisplay = state.sortedNodes;

    const previousVisibleNodes = usePrevious<typeof visibleNodes | undefined>(
        visibleNodes,
        undefined
    );
    const previousRootNodeId = usePrevious<number | undefined>(
        rootNode.id,
        undefined
    );

    React.useEffect(() => {
        const isInitialRender =
            previousVisibleNodes === undefined || previousRootNodeId === undefined;
        const hasVisibleNodesLoaded =
            isInitialRender || previousVisibleNodes !== visibleNodes;
        const hasRootNodeChanged =
            isInitialRender || previousRootNodeId !== rootNode.id;

        LogWorkflowNavigation("RenderChildNodes:effect", {
            rootNodeId: rootNode.id,
            previousRootNodeId,
            hasVisibleNodesLoaded,
            hasRootNodeChanged,
            visibleNodeCount: visibleNodes.length,
        });

        if (hasVisibleNodesLoaded || hasRootNodeChanged) {
            let isCurrent = true;
            const setStateIfCurrent: React.Dispatch<
                React.SetStateAction<IRenderChildNodesState>
            > = (update) => {
                if (isCurrent) {
                    setState(update);
                }
            };

            LoadChildNodeData(rootNode, visibleNodes, setStateIfCurrent);

            return () => {
                isCurrent = false;
            };
        }
    }, [rootNode.id, visibleNodes]);

    React.useEffect(
        () => ScrollToBottomIfLarge(`scrollTarget_renderChildNodes`),
        [nodesToDisplay]
    );

    ShareState(ESharedFunction.RenderChildNodes_state, state, setState);

    return (
        <div id="scrollTarget_renderChildNodes" style={{ marginTop: `7px` }}>
            <DisplayRemoveOutdatedTasksLink
                rootNode={rootNode}
                childNodes={state.immediateChildren}
            />
            <DisplayViewChronologicalNote rootNode={rootNode} />
            {nodesToDisplay.map((node, index) => (
                <RenderChildNode
                    key={node.id}
                    {...{ ...props, index }}
                    node={node}
                />
            ))}
            <CompleteAllTodayNodesLink {...{ rootNode, state }} />
            <SetSortTarget
                sourceNode={sortSource}
                rootNode={rootNode}
                sortMode={sortMode}
                renderLevel={level}
            />
        </div>
    );
}
